import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { SystemStats, User } from '@/types'
import type { AdminOrgRow, AdminOrgStatus } from '@/types/admin'
import { adminApi } from '@/lib/api'

// The /admin endpoints answer with the body itself (no { data } envelope) except the org list,
// whose body is { data, total, page, limit }. Accept both so a wrapped reply keeps working.
const bodyOf = <T>(response: unknown): T => ((response as { data?: T } | null)?.data ?? response) as T

export const useAdminStore = defineStore('admin', () => {
  const organizations = ref<AdminOrgRow[]>([])
  const currentOrganization = ref<AdminOrgRow | null>(null)
  const currentOrgUsers = ref<User[]>([])
  const systemStats = ref<SystemStats | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)
  const pagination = ref({
    page: 1,
    limit: 20,
    total: 0,
  })

  const totalPages = computed(() => Math.ceil(pagination.value.total / pagination.value.limit))

  // Only the latest list request may write the list: a slow answer to an older search or filter
  // must not overwrite a newer one (the page reloads on every keystroke pause and segment click).
  let listRequest = 0

  const fetchOrganizations = async (params?: {
    page?: number
    limit?: number
    search?: string
    status?: AdminOrgStatus
    hideDemo?: boolean
  }) => {
    const request = ++listRequest
    loading.value = true
    error.value = null
    try {
      const response = await adminApi.listOrganizations({
        page: params?.page || pagination.value.page,
        limit: params?.limit || pagination.value.limit,
        ...(params?.search != null && { search: params.search }),
        ...(params?.status && { status: params.status }),
        ...(params?.hideDemo && { hideDemo: true }),
      })
      if (request !== listRequest) return
      organizations.value = response.data as any
      pagination.value = {
        page: (response as any).page,
        limit: (response as any).limit,
        total: (response as any).total,
      }
    } catch (err: any) {
      if (request !== listRequest) return
      error.value = err.response?.data?.message || 'Failed to fetch organizations'
      throw err
    } finally {
      if (request === listRequest) loading.value = false
    }
  }

  const fetchOrganization = async (id: string) => {
    loading.value = true
    error.value = null
    try {
      const response = await adminApi.getOrganization(id)
      currentOrganization.value = bodyOf<AdminOrgRow>(response)
      return currentOrganization.value
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to fetch organization'
      throw err
    } finally {
      loading.value = false
    }
  }

  const fetchOrganizationUsers = async (orgId: string) => {
    loading.value = true
    error.value = null
    try {
      const response = await adminApi.getOrganizationUsers(orgId)
      currentOrgUsers.value = response.data
      return response.data
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to fetch organization users'
      throw err
    } finally {
      loading.value = false
    }
  }

  const updateUserLimit = async (orgId: string, freeSeats: number) => {
    loading.value = true
    error.value = null
    try {
      const response = await adminApi.updateUserLimit(orgId, freeSeats)
      // Update in local state
      const index = organizations.value.findIndex(org => org._id === orgId)
      const updatedOrg = bodyOf<AdminOrgRow>(response)
      if (index !== -1) {
        organizations.value[index] = updatedOrg
      }
      if (currentOrganization.value?._id === orgId) {
        currentOrganization.value = updatedOrg
      }
      return updatedOrg
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to update user limit'
      throw err
    } finally {
      loading.value = false
    }
  }

  const updateAiComplimentary = async (orgId: string, aiComplimentary: boolean) => {
    const response = await adminApi.updateAiComplimentary(orgId, aiComplimentary)
    const updatedOrg = bodyOf<AdminOrgRow>(response)
    const index = organizations.value.findIndex(org => org._id === orgId)
    if (index !== -1) organizations.value[index] = updatedOrg
    if (currentOrganization.value?._id === orgId) currentOrganization.value = updatedOrg
    return updatedOrg
  }

  const fetchSystemStats = async () => {
    loading.value = true
    error.value = null
    try {
      const response = await adminApi.getSystemStats()
      systemStats.value = bodyOf<SystemStats>(response)
      return systemStats.value
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to fetch system stats'
      throw err
    } finally {
      loading.value = false
    }
  }

  const setPage = (page: number) => {
    pagination.value.page = page
  }

  const clearCurrentOrganization = () => {
    currentOrganization.value = null
    currentOrgUsers.value = []
  }

  return {
    // State
    organizations,
    currentOrganization,
    currentOrgUsers,
    systemStats,
    loading,
    error,
    pagination,
    // Computed
    totalPages,
    // Actions
    fetchOrganizations,
    fetchOrganization,
    fetchOrganizationUsers,
    updateUserLimit,
    updateAiComplimentary,
    fetchSystemStats,
    setPage,
    clearCurrentOrganization,
  }
})
